// Considered: ChannelSettingsTabOrganism — rejected because it is the legacy 4-tab panel without glyphKind/hue/preview card. See spec Reuse Audit.
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { uploadChannelLogo, deleteChannelLogo } from '../../../api/marketplaceApi';
import type { Channel } from 'types/channel';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import ScopeCoinAtom from '../../atoms/marketplace/ScopeCoinAtom';
import HuePickerMolecule from '../../molecules/marketplace/HuePickerMolecule';
import IconPickerAtom from '../../atoms/IconPicker/IconPickerAtom';
import UploadAttachmentMolecule from '../../molecules/UploadAttachment/UploadAttachmentMolecule';
import './ChannelBrandingSectionOrganism.scss';

type IconMode = 'letters' | 'icon' | 'image';

export interface ChannelBrandingDraft {
  glyph: string;
  glyphKind: 'letter' | 'icon';
  hue: number;
}

export interface ChannelBrandingSectionOrganismProps {
  channel: Channel;
  draft: ChannelBrandingDraft;
  update: (patch: Partial<ChannelBrandingDraft>) => void;
  onLogoChanged: () => void;
}

function deriveIconMode(channel: Channel): IconMode {
  if (channel.logoUrl) return 'image';
  return 'letters';
}

const ChannelBrandingSectionOrganism: React.FC<ChannelBrandingSectionOrganismProps> = ({
  channel,
  draft,
  update,
  onLogoChanged,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const [iconMode, setIconMode] = useState<IconMode>(() =>
    deriveIconMode(channel)
  );
  const [showLogoDropzone, setShowLogoDropzone] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  const displayName = getLocalizedText(channel.name);

  // Sync iconMode only when channel.logoUrl changes (after upload/delete + refetch)
  useEffect(() => {
    if (channel.logoUrl) {
      setIconMode('image');
    } else if (iconMode === 'image') {
      // Logo was removed — fall back to letters
      setIconMode('letters');
      setShowLogoDropzone(false);
    }
  }, [channel.logoUrl]);

  const handleModeChange = useCallback(
    (mode: IconMode) => {
      setIconMode(mode);
      if (mode === 'letters') {
        const initial = displayName.charAt(0).toUpperCase() || 'C';
        update({ glyphKind: 'letter', glyph: draft.glyph && !draft.glyph.includes(':') ? draft.glyph : initial });
        setShowLogoDropzone(false);
      } else if (mode === 'icon') {
        const iconGlyph = draft.glyph.includes(':') ? draft.glyph : 'lucide:circle';
        update({ glyphKind: 'icon', glyph: iconGlyph });
        setShowLogoDropzone(false);
      } else {
        // image — logo upload is immediate, not draft-based
        setShowLogoDropzone(true);
      }
    },
    [draft.glyph, displayName, update]
  );

  const handleLogoUpload = useCallback(
    async (file: File) => {
      if (!apiClients) return;
      setLogoUploading(true);
      try {
        await uploadChannelLogo(apiClients.schemaClient, channel.id, file);
        setShowLogoDropzone(false);
        onLogoChanged();
      } finally {
        setLogoUploading(false);
      }
    },
    [apiClients, channel.id, onLogoChanged]
  );

  const handleLogoRemove = useCallback(async () => {
    if (!apiClients) return;
    await deleteChannelLogo(apiClients.schemaClient, channel.id);
    setIconMode('letters');
    setShowLogoDropzone(false);
    onLogoChanged();
  }, [apiClients, channel.id, onLogoChanged]);

  return (
    <div className="channel-branding-section">
      {/* ── Channel icon ───────────────────────────────────────────── */}
      <div className="channel-branding-section__card">
        <div className="channel-branding-section__card-header">
          <h3 className="channel-branding-section__card-title">
            {t('marketplace.channelManage.v3Branding.iconTitle')}
          </h3>
          <p className="channel-branding-section__card-subtitle">
            {t('marketplace.channelManage.v3Branding.iconSubtitle')}
          </p>
        </div>

        <div className="channel-branding-section__icon-row">
          {/* Preview glyph / logo */}
          {channel.logoUrl ? (
            <img
              src={channel.logoUrl}
              alt={t('marketplace.channelManage.v3Branding.logoPreviewAlt')}
              className="channel-branding-section__logo-img"
            />
          ) : (
            <ScopeCoinAtom
              scope={channel.scope}
              glyph={draft.glyph || null}
              glyphKind={draft.glyphKind}
              channelName={displayName}
              hue={draft.hue}
              size="xl"
              className="channel-branding-section__glyph-preview"
            />
          )}

          <div className="channel-branding-section__icon-controls">
            {/* 3-way mode selector */}
            <div
              className="channel-branding-section__mode-btns"
              role="group"
              aria-label={t('marketplace.channelManage.v3Branding.iconModeLabel')}
            >
              <button
                type="button"
                className={`channel-branding-section__mode-btn${iconMode === 'letters' ? ' is-active' : ''}`}
                aria-pressed={iconMode === 'letters'}
                onClick={() => handleModeChange('letters')}
              >
                <i className="pi pi-font" aria-hidden="true" />
                {t('marketplace.channelManage.v3Branding.useLetters')}
              </button>
              <button
                type="button"
                className={`channel-branding-section__mode-btn${iconMode === 'icon' ? ' is-active' : ''}`}
                aria-pressed={iconMode === 'icon'}
                onClick={() => handleModeChange('icon')}
              >
                <i className="pi pi-star" aria-hidden="true" />
                {t('marketplace.channelManage.v3Branding.chooseIcon')}
              </button>
              <button
                type="button"
                className={`channel-branding-section__mode-btn${iconMode === 'image' ? ' is-active' : ''}`}
                aria-pressed={iconMode === 'image'}
                onClick={() => handleModeChange('image')}
              >
                <i className="pi pi-image" aria-hidden="true" />
                {t('marketplace.channelManage.v3Branding.uploadImage')}
              </button>
            </div>

            {/* Letters mode — initials input */}
            {iconMode === 'letters' && !channel.logoUrl && (
              <div className="channel-branding-section__field">
                <label
                  htmlFor="channel-branding-glyph"
                  className="channel-branding-section__label"
                >
                  {t('marketplace.channelManage.v3Branding.initialsLabel')}
                </label>
                <input
                  id="channel-branding-glyph"
                  type="text"
                  maxLength={2}
                  className="channel-branding-section__initials-input"
                  placeholder={t('marketplace.channelManage.v3Branding.initialsPlaceholder')}
                  value={draft.glyph}
                  onChange={(e) =>
                    update({ glyph: e.target.value.slice(0, 2).toUpperCase() })
                  }
                />
                <p className="channel-branding-section__hint">
                  {t('marketplace.channelManage.v3Branding.initialsHint')}
                </p>
              </div>
            )}

            {/* Icon mode — icon picker */}
            {iconMode === 'icon' && !channel.logoUrl && (
              <IconPickerAtom
                value={draft.glyph.includes(':') ? draft.glyph : undefined}
                onChange={(icon) => update({ glyph: icon, glyphKind: 'icon' })}
                tooltip={t('marketplace.channelManage.v3Branding.iconPickerHint')}
              />
            )}

            {/* Image mode — upload / remove controls */}
            {iconMode === 'image' && (
              <div className="channel-branding-section__upload-controls">
                {channel.logoUrl ? (
                  <button
                    type="button"
                    className="channel-branding-section__btn-ghost"
                    onClick={handleLogoRemove}
                  >
                    <i className="pi pi-trash" aria-hidden="true" />
                    {t('marketplace.channelManage.v3Branding.removeLogo')}
                  </button>
                ) : (
                  <>
                    {channel.logo_attachment_id && !channel.logoUrl && (
                      <span className="channel-branding-section__scanning">
                        <i className="pi pi-spin pi-spinner" aria-hidden="true" />
                        {t('marketplace.channelManage.v3Branding.scanning')}
                      </span>
                    )}
                    {showLogoDropzone && (
                      <UploadAttachmentMolecule
                        onUpload={handleLogoUpload}
                        accept="image/*"
                        maxSizeBytes={2 * 1024 * 1024}
                        disabled={logoUploading}
                        className="channel-branding-section__logo-dropzone"
                      />
                    )}
                    <p className="channel-branding-section__hint">
                      {t('marketplace.channelManage.v3Branding.logoHint')}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Accent color ───────────────────────────────────────────── */}
      <div className="channel-branding-section__card">
        <div className="channel-branding-section__card-header">
          <h3 className="channel-branding-section__card-title">
            {t('marketplace.channelManage.v3Branding.accentTitle')}
          </h3>
          <p className="channel-branding-section__card-subtitle">
            {t('marketplace.channelManage.v3Branding.accentSubtitle')}
          </p>
        </div>
        <HuePickerMolecule
          value={draft.hue}
          onChange={(hue) => update({ hue })}
          className="channel-branding-section__hue-picker"
        />
      </div>

      {/* ── Marketplace-card preview ────────────────────────────────── */}
      <div className="channel-branding-section__card">
        <div className="channel-branding-section__card-header">
          <h3 className="channel-branding-section__card-title">
            {t('marketplace.channelManage.v3Branding.previewTitle')}
          </h3>
          <p className="channel-branding-section__card-subtitle">
            {t('marketplace.channelManage.v3Branding.previewSubtitle')}
          </p>
        </div>
        <div className="channel-branding-section__preview-bg">
          <div className="channel-branding-section__preview-card">
            {channel.logoUrl ? (
              <img
                src={channel.logoUrl}
                alt={t('marketplace.channelManage.v3Branding.logoPreviewAlt')}
                className="channel-branding-section__preview-logo"
              />
            ) : (
              <ScopeCoinAtom
                scope={channel.scope}
                glyph={draft.glyph || null}
                glyphKind={draft.glyphKind}
                channelName={displayName}
                hue={draft.hue}
                size="md"
              />
            )}
            <div className="channel-branding-section__preview-info">
              <span className="channel-branding-section__preview-name">{displayName}</span>
              <span className="channel-branding-section__preview-meta">
                {t('marketplace.channelManage.v3Branding.previewMeta')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ChannelBrandingSectionOrganism.displayName = 'ChannelBrandingSectionOrganism';

export default ChannelBrandingSectionOrganism;
